import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, HardHat, LogOut, Menu, ScanLine, Send, UserCheck, X,
} from "lucide-react";
import QRScanner from "@/components/QRScanner";

/**
 * Le due barre della visita.
 *
 * Chi entra con un codice se le trova addosso per tutto il giro. Sopra si
 * naviga: il pulsante per inquadrare un altro QR e il menu di cio' che gli e'
 * aperto - che decide il server, non questa schermata. Sotto si agisce:
 * segnala un rilievo, oppure esci.
 *
 * Stanno separate apposta. Su un telefono il pollice arriva in fondo, e
 * "Termina" non deve capitare sotto il dito mentre si cerca di scansionare.
 *
 * Sono qui e non nel menu dell'app perche' devono comparire anche sulle
 * pagine dei QR, che quel menu non ce l'hanno.
 *
 * Il conto dei trenta minuti che si vede qui e' una cortesia - avvisa e porta
 * fuori. Quello che conta davvero lo tiene il server, che a ogni richiesta
 * controlla da quanto la visita e' ferma e la chiude.
 */

const CHIAVE = "sgm_visita";

export function visitaAperta() {
  try {
    const grezzo = localStorage.getItem(CHIAVE);
    return grezzo ? JSON.parse(grezzo) : null;
  } catch {
    return null;
  }
}

/** La visita che si sta facendo, tenuta a portata delle pagine dei QR. */
export function ricordaVisita(v) {
  salvaVisita(v && { id: v.id, nome: v.nome, azienda: v.azienda });
  window.dispatchEvent(new CustomEvent("sgm-visita-aggiornata"));
}

function salvaVisita(v) {
  try {
    if (v) localStorage.setItem(CHIAVE, JSON.stringify(v));
    else localStorage.removeItem(CHIAVE);
  } catch {
    /* un browser che non lascia scrivere non deve rompere la visita */
  }
}

/**
 * Segna una tappa del giro. La chiamano le pagine pubbliche dei QR.
 *
 * Non fa rumore se fallisce: chi apre quel QR senza essere in visita - un
 * tecnico, un passante - deve vedere la pagina come sempre.
 */
export function segnaTappa(tipo, riferimento, nome) {
  const v = visitaAperta();
  if (!v?.id || !riferimento) return;
  api.post(`/visite/${v.id}/tappa`, { tipo, riferimento, nome: nome || null })
    .catch(() => {});
}

// -----------------------------------------------------------------------------
export default function VisitaOspite() {
  const { user, logout } = useAuth();
  const [visita, setVisita] = useState(() => visitaAperta());
  const idVisita = user?.visita_id;
  const [riferimenti, setRiferimenti] = useState(null);
  const [segnala, setSegnala] = useState(false);
  const [scanner, setScanner] = useState(false);
  const [menuAperto, setMenuAperto] = useState(false);
  const [menu, setMenu] = useState(null);
  const scadenza = useRef(null);

  // Si cambia pagina con un ricaricamento e non con il router: le pagine dei
  // QR stanno fuori dal menu dell'app, e cosi' ognuna riparte pulita.
  const vaiA = (percorso) => { window.location.href = percorso; };

  const ospite = user?.role === "visitatore";

  useEffect(() => {
    const aggiorna = () => setVisita(visitaAperta());
    window.addEventListener("sgm-visita-aggiornata", aggiorna);
    return () => window.removeEventListener("sgm-visita-aggiornata", aggiorna);
  }, []);

  useEffect(() => {
    if (!ospite) return;
    api.get("/visite/riferimenti").then(({ data }) => setRiferimenti(data)).catch(() => {});
  }, [ospite]);

  // Cosa gli e' aperto lo dice il server: qui si disegna e basta.
  useEffect(() => {
    if (!ospite || !idVisita) return;
    api.get(`/visite/${idVisita}/menu`).then(({ data }) => setMenu(data)).catch(() => {});
  }, [ospite, idVisita]);

  // La visita salvata potrebbe essere gia' chiusa: succede se il tablet e'
  // rimasto fermo e il server l'ha chiusa per inattivita'.
  useEffect(() => {
    if (!ospite || !idVisita) return;
    api.get(`/visite/${idVisita}`)
      .then(({ data }) => {
        if (!data.aperta) { salvaVisita(null); setVisita(null); return; }
        const fresca = { id: data.id, nome: data.nome, azienda: data.azienda };
        salvaVisita(fresca); setVisita(fresca);
      })
      .catch(() => { salvaVisita(null); setVisita(null); });
  }, [ospite, idVisita]);

  const chiudi = useCallback(async (motivo) => {
    const v = visitaAperta();
    if (v?.id) {
      try { await api.post(`/visite/${v.id}/chiudi`, { motivo }); } catch { /* esce comunque */ }
    }
    salvaVisita(null);
    setVisita(null);
    await logout();
    window.location.href = "/visita";
  }, [logout]);

  // Il conto alla rovescia dell'inattivita'. Ogni tocco lo fa ripartire, e un
  // battito al server dice che la sessione e' ancora viva.
  useEffect(() => {
    if (!ospite || !visita?.id) return undefined;
    const minuti = riferimenti?.minuti_inattivita || 30;
    let ultimoBattito = 0;

    const riparti = () => {
      clearTimeout(scadenza.current);
      scadenza.current = setTimeout(() => {
        toast.error("Visita chiusa per inattività");
        chiudi("inattivita");
      }, minuti * 60 * 1000);
      const ora = Date.now();
      if (ora - ultimoBattito > 120000) {
        ultimoBattito = ora;
        api.post(`/visite/${visita.id}/attivo`).catch(() => {});
      }
    };

    const eventi = ["pointerdown", "keydown", "scroll", "visibilitychange"];
    eventi.forEach((e) => window.addEventListener(e, riparti, { passive: true }));
    riparti();
    return () => {
      clearTimeout(scadenza.current);
      eventi.forEach((e) => window.removeEventListener(e, riparti));
    };
  }, [ospite, visita?.id, riferimenti, chiudi]);

  if (!ospite || !visita) return null;

  return (
    <>
      {/* In alto si naviga: il QR nuovo e le cose che gli sono aperte.
          In basso si agisce: segnala, oppure esci. Le due cose stanno
          separate perche' su un telefono il pollice arriva in fondo, e
          "Termina" non deve trovarsi sotto il dito per sbaglio. */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-[#0067b2] text-white shadow-md"
           data-testid="barra-visita-alto">
        <div className="px-3 py-2 flex items-center gap-2">
          <button type="button" onClick={() => setMenuAperto(!menuAperto)}
                  data-testid="menu-visita"
                  className="shrink-0 w-10 h-10 rounded-lg hover:bg-white/15 flex items-center justify-center">
            {menuAperto ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] uppercase tracking-widest opacity-75">
              In visita
            </span>
            <span className="block text-sm font-semibold truncate leading-tight">
              {menu?.cantiere_nome || visita.nome}
            </span>
          </span>
          <Button size="sm" onClick={() => setScanner(true)}
                  data-testid="nuovo-qr"
                  className="bg-white text-[#0067b2] hover:bg-slate-100 h-10 shrink-0">
            <ScanLine className="w-4 h-4 mr-1.5" /> Nuovo QR
          </Button>
        </div>

        {menuAperto && (
          <div className="border-t border-white/20 bg-[#00589b] px-3 py-2 space-y-1">
            {(menu?.voci || []).map((v) => (
              <button key={v.codice} type="button"
                      data-testid={`voce-menu-${v.codice}`}
                      onClick={() => { setMenuAperto(false); vaiA(v.percorso); }}
                      className="w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-white/10">
                <HardHat className="w-4 h-4 shrink-0 opacity-80" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">{v.nome}</span>
                  {v.descrizione && (
                    <span className="block text-xs opacity-75 truncate">{v.descrizione}</span>
                  )}
                </span>
              </button>
            ))}
            <p className="text-[11px] opacity-70 px-3 pt-1 pb-0.5">
              Il resto del gestionale non è compreso nella visita. Per vedere una
              scheda, inquadrate il suo QR.
            </p>
          </div>
        )}
      </div>
      <div className="h-14" aria-hidden />

      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0067b2] text-white shadow-lg"
           data-testid="barra-visita">
        <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
          <span className="min-w-0 flex items-center gap-2">
            <UserCheck className="w-4 h-4 shrink-0" />
            <span className="text-sm truncate">
              <span className="font-semibold">{visita.nome}</span>
              <span className="opacity-80 hidden sm:inline"> · {visita.azienda}</span>
            </span>
          </span>
          <span className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={() => setSegnala(true)}
                    data-testid="apri-difformita"
                    className="bg-orange-500 hover:bg-orange-600 text-white h-9">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Segnala
            </Button>
            <Button size="sm" variant="outline" onClick={() => chiudi("uscita")}
                    data-testid="termina-visita"
                    className="h-9 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">
              <LogOut className="w-4 h-4 mr-1.5" /> Termina
            </Button>
          </span>
        </div>
      </div>
      <div className="h-14" aria-hidden />

      {scanner && (
        <QRScanner pubblico
                   onClose={() => setScanner(false)}
                   onScanned={(target) => {
                     setScanner(false);
                     toast.success(`${target.label} riconosciuto`);
                     vaiA(target.path);
                   }} />
      )}

      {segnala && (
        <ModuloDifformita visita={visita} riferimenti={riferimenti}
                          onChiudi={() => setSegnala(false)}
                          onFatta={() => setSegnala(false)} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
function ModuloDifformita({ visita, riferimenti, onChiudi, onFatta }) {
  const [descrizione, setDescrizione] = useState("");
  const [gravita, setGravita] = useState("difformita");
  const [dove, setDove] = useState("");
  const [invio, setInvio] = useState(false);

  const manda = async () => {
    if (!descrizione.trim()) { toast.error("Scrivi cosa hai riscontrato"); return; }
    setInvio(true);
    try {
      await api.post(`/visite/${visita.id}/difformita`,
                     { descrizione, gravita, dove: dove || null });
      toast.success("Rilievo inviato in azienda");
      onFatta();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setInvio(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
         data-testid="modulo-difformita">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl p-4 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-display text-xl font-black tracking-tight">
              Segnala un rilievo
            </h2>
            <p className="text-xs text-slate-500">
              Resta scritto a nome vostro e arriva subito in azienda.
            </p>
          </div>
          <button type="button" onClick={onChiudi} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Gravità</Label>
          <div className="flex gap-2 mt-1">
            {(riferimenti?.gravita || []).map((g) => (
              <button key={g.codice} type="button" onClick={() => setGravita(g.codice)}
                      data-testid={`gravita-${g.codice}`}
                      className={`flex-1 h-11 rounded-lg border text-sm font-medium ${
                        gravita === g.codice
                          ? g.codice === "grave" ? "bg-red-500 text-white border-red-500"
                            : g.codice === "difformita" ? "bg-amber-500 text-white border-amber-500"
                            : "bg-sky-600 text-white border-sky-600"
                          : "bg-white text-slate-600 border-slate-200"}`}>
                {g.nome}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Dove</Label>
          <Input value={dove} onChange={(e) => setDove(e.target.value)} className="h-11 mt-1"
                 placeholder="Scavo lato ovest, deposito, quadro elettrico..." />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Cosa avete riscontrato
          </Label>
          <textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
                    data-testid="difformita-descrizione"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[110px] mt-1"
                    placeholder="Descrivete il rilievo come lo scrivereste sul verbale." />
        </div>

        <Button onClick={manda} disabled={invio}
                data-testid="manda-difformita"
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white">
          <Send className="w-4 h-4 mr-2" /> {invio ? "Invio..." : "Registra il rilievo"}
        </Button>
      </div>
    </div>
  );
}
